import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PermissionBit } from '../utils/permission-types';
import { InvoicingGroupAdminRole } from '../models/group.model';
import { Permission } from '../enums/permission.enum';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private cachedPermissions: bigint = 0n;
  private permissionsSubject = new BehaviorSubject<bigint>(0n);
  readonly permissions$: Observable<bigint> = this.permissionsSubject.asObservable();

  private readySubject = new BehaviorSubject<boolean>(false);
  readonly ready$: Observable<boolean> = this.readySubject.asObservable();

  getUserPermissions(): bigint {
    return this.cachedPermissions;
  }

  updatePermissions(permissions: bigint): void {
    this.cachedPermissions = permissions;
    this.permissionsSubject.next(permissions);
    this.readySubject.next(true);
  }

  clearPermissions(): void {
    this.cachedPermissions = 0n;
    this.permissionsSubject.next(0n);
    this.readySubject.next(false);
  }

  hasPermission(permission: PermissionBit): boolean {
    return (this.cachedPermissions & permission) === permission;
  }

  hasAnyPermission(permissions: ReadonlyArray<PermissionBit>): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  hasAllPermissions(permissions: ReadonlyArray<PermissionBit>): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  canUseMyPeopleFilters(): boolean {
    return this.hasAnyPermission([Permission.PEOPLE_READ, Permission.MANAGER_READ, Permission.MANAGER_PEOPLE]);
  }

  isReady(): boolean {
    return this.readySubject.value === true;
  }

  // ── Group-level permission helpers (usability only; backend is authoritative) ──

  /** INVOICING_ADMIN or any group role (READ, READ_WRITE, MANAGE) */
  canReadGroup(role: InvoicingGroupAdminRole | null | undefined): boolean {
    if (this.hasPermission(Permission.INVOICING_ADMIN)) return true;
    return role === 'READ' || role === 'READ_WRITE' || role === 'MANAGE';
  }

  /** INVOICING_ADMIN or READ_WRITE / MANAGE */
  canWriteGroup(role: InvoicingGroupAdminRole | null | undefined): boolean {
    if (this.hasPermission(Permission.INVOICING_ADMIN)) return true;
    return role === 'READ_WRITE' || role === 'MANAGE';
  }

  /** INVOICING_ADMIN or MANAGE only */
  canManageGroup(role: InvoicingGroupAdminRole | null | undefined): boolean {
    if (this.hasPermission(Permission.INVOICING_ADMIN)) return true;
    return role === 'MANAGE';
  }
}
